import { useCallback, useRef, useState } from "react";

export interface UseLongPressOptions {
    onLongPress: (e?: React.SyntheticEvent) => void;
    onClick?: (e?: React.SyntheticEvent) => void;
    threshold?: number;      // Tempo a segurar em ms (padrão 450ms)
    moveThreshold?: number;  // Tolerância de movimento do dedo em px (padrão 25px)
    vibrate?: number;        // Duração da vibração háptica em ms (padrão 40ms)
}

export const useLongPress = ({
    onLongPress,
    onClick,
    threshold = 450,
    moveThreshold = 25,
    vibrate = 40,
}: UseLongPressOptions) => {
    const timerRef = useRef<NodeJS.Timeout | null>(null);
    const isLongPressTriggered = useRef<boolean>(false);
    const startCoord = useRef<{ x: number; y: number }>({ x: 0, y: 0 });
    const [isPressing, setIsPressing] = useState<boolean>(false);

    const cancelTimer = useCallback(() => {
        if (timerRef.current) {
            clearTimeout(timerRef.current);
            timerRef.current = null;
        }
        setIsPressing(false);
    }, []);

    const handleStart = useCallback(
        (e: React.TouchEvent | React.MouseEvent) => {
            isLongPressTriggered.current = false;
            setIsPressing(true);

            if ("touches" in e && e.touches.length > 0) {
                startCoord.current = {
                    x: e.touches[0].clientX,
                    y: e.touches[0].clientY,
                };
            } else if ("clientX" in e) {
                startCoord.current = {
                    x: e.clientX,
                    y: e.clientY,
                };
            }

            timerRef.current = setTimeout(() => {
                isLongPressTriggered.current = true;
                setIsPressing(false);

                // Vibração háptica no dispositivo móvel
                if (typeof navigator !== "undefined" && navigator.vibrate) {
                    try {
                        navigator.vibrate(vibrate);
                    } catch {
                        // Silencia se não for suportado
                    }
                }

                onLongPress(e);
            }, threshold);
        },
        [onLongPress, threshold, vibrate]
    );

    const handleTouchMove = useCallback(
        (e: React.TouchEvent) => {
            if (!timerRef.current) return;
            const touch = e.touches[0];
            const diffX = Math.abs(touch.clientX - startCoord.current.x);
            const diffY = Math.abs(touch.clientY - startCoord.current.y);

            // Se for scroll real (> 25px), cancela o long press
            if (diffX > moveThreshold || diffY > moveThreshold) {
                cancelTimer();
            }
        },
        [cancelTimer, moveThreshold]
    );

    const handleClick = useCallback(
        (e: React.MouseEvent) => {
            // Se foi acionado pelo long-press, anula o clique sintético do browser
            if (isLongPressTriggered.current) {
                e.preventDefault();
                e.stopPropagation();
                isLongPressTriggered.current = false;
                return;
            }

            // Apenas executa se tiver sido passado um onClick explícito
            if (onClick) {
                onClick(e);
            }
        },
        [onClick]
    );

    const handleContextMenu = useCallback((e: React.SyntheticEvent) => {
        // Previne o menu de contexto nativo ao segurar o dedo
        e.preventDefault();
    }, []);

    return {
        isPressing,
        handlers: {
            onTouchStart: handleStart,
            onTouchEnd: cancelTimer,
            onTouchMove: handleTouchMove,
            onMouseDown: handleStart,
            onMouseUp: cancelTimer,
            onMouseLeave: cancelTimer,
            onClick: handleClick,
            onContextMenu: handleContextMenu,
        },
    };
};

export default useLongPress;